// External dependencies
import get from 'lodash.get';
import React from 'react';
import FaPlayCircle from 'react-icons/lib/fa/play-circle';
import * as Sentry from '@sentry/browser';
import { COPY_STRINGS } from '../../constants/translatedCopy';
import {getUserData} from '../../services/API';
import doNotUseAccessibleStaticElementInteractions from "A11y/doNotUseAccessibleStaticElementInteractions.js";
import { prepareMicForPlayback, prepareMicForRecord } from '../audiocapture/recorder';
import { logSessionInfo } from '../../services/logger';

// Guards against a nonsense duration arming a nonsense timer -- notably one long enough to
// overflow setTimeout's ~24.8 day ceiling, which fires immediately and cuts the video off.
// Callers playing anything longer than this must raise it; see isValidDuration.
const DEFAULT_MAX_DURATION_SECONDS = 90;

class StudentVideo extends React.Component {

  constructor(props){
    super(props);
    this.onEnd = this.onEnd.bind(this);
    this.onError = this.onError.bind(this);
    this.handleClick = this.handleClick.bind(this);
    this.onLoadedMetadata = this.onLoadedMetadata.bind(this);
    this.onLoadedData = this.onLoadedData.bind(this);
    this.onPlay = this.onPlay.bind(this);
    this.onTimeUpdate = this.onTimeUpdate.bind(this);
    this.simplePlayVideo = this.simplePlayVideo.bind(this);
    this.playVideo = this.playVideo.bind(this)
    
    this.state = {
      clickable: getUserData().sessionData.useIpadFallbacks,
      playedVideo: null,
      requirePlayButton: false
    }
    
    // Instance-level variables to avoid race conditions with module-level variables
    this.videoTimer = null;
    this.videoTimeout = 0;
    this.hasCalledOnEnd = false;
    this.reportedPlaybackStartFor = null;
    this.reportedDurationRejectionFor = null;

    this.htmlVideoPlayerRef = React.createRef();
  }

  componentDidMount(){
    this.videoTimer = null;
    this.hasCalledOnEnd = false;

    if(this.props.autoPlay && !this.state.clickable){
      this.loadAndPlayVideo();
    }
  }

  componentDidUpdate(prevProps){
    if(!this.state.clickable){
      if(prevProps.videoURL !== this.props.videoURL && !this.props.videoURL){
        this.cleanUp();
      }
      if(prevProps.videoURL !== this.props.videoURL && this.props.videoURL){
        // Reset hasCalledOnEnd for new video - ensures onEnd can fire for the new video
        this.hasCalledOnEnd = false;
        if(this.props.autoPlay){
          this.cleanUp();
          this.loadAndPlayVideo();
        }else{
          this.cleanUp();
          this.loadVideo();
        }
      }
      if(!prevProps.autoPlay && this.props.autoPlay && this.props.videoURL){
        this.playVideo();
      }
    }
  }

  onLoadedMetadata(event){
    // Remove timeout logic from here - it will be handled in onPlay
    // This ensures timeout only starts when video actually begins playing
  }

  onLoadedData(event){
    this.playVideo();
  }

  /**
   * The configured ceiling, falling back to the default for a caller that passes null.
   * defaultProps only fills in undefined, so an explicit null would otherwise make every
   * comparison below false and disable the fallbacks wholesale — the exact failure this
   * prop exists to stop being silent.
   */
  maxDurationSeconds(){
    return this.props.maxDurationSeconds || DEFAULT_MAX_DURATION_SECONDS;
  }

  /**
   * Validates that a video duration value is trustworthy.
   * Returns true if duration is a positive, finite number within reasonable bounds.
   *
   * The upper bound is what every completion fallback below is gated on, so a caller
   * playing something longer than maxDurationSeconds loses all of them and is left
   * depending on the native ended event alone — the one thing those fallbacks exist to
   * cover for. Raise it via the prop rather than playing a longer video under the default,
   * and note that a rejection on those grounds is logged: the point is that this stops
   * being something you have to go and diagnose.
   */
  isValidDuration(duration){
    if(!isFinite(duration) || duration <= 0) return false; // NaN, Infinity, live-stream sentinels
    if(duration < this.maxDurationSeconds()) return true;

    this.reportDurationRejected(duration);
    return false;
  }

  /**
   * A real duration that overshoots the ceiling is the one rejection worth hearing about —
   * it means this video is running without any completion fallback. Once per URL, because
   * onTimeUpdate asks twice a go, several times a second.
   */
  reportDurationRejected(duration){
    if(this.reportedDurationRejectionFor === this.props.videoURL) return;
    this.reportedDurationRejectionFor = this.props.videoURL;

    logSessionInfo("video_duration_rejected", {
      videoURL: this.props.videoURL,
      duration,
      maxDurationSeconds: this.maxDurationSeconds(),
    });
    console.warn("Video duration above the supported ceiling - completion fallbacks are disabled for this video", this.props.videoURL, duration);
  }

  onPlay(event){
    const player = this.htmlVideoPlayerRef.current;

    // Start the timeout only when the video actually begins playing
    // This ensures that videos blocked by autoplay restrictions don't trigger premature timeouts
    // Clear any existing timer to avoid duplicates if onPlay fires multiple times (e.g., buffering)
    if(this.videoTimer){
      window.clearTimeout(this.videoTimer);
      this.videoTimer = null;
    }
    // NOTE: Do NOT reset hasCalledOnEnd here. onPlay can fire multiple times during
    // a single video playback (buffering, resume). Resetting here creates a race condition
    // where onTimeUpdate triggers onEnd, then video resumes (firing onPlay), then onEnded
    // fires causing duplicate onDone callbacks. Reset only happens in loadAndPlayVideo().

    // Only set a new timer if we haven't already called onEnd for this video
    if(!this.hasCalledOnEnd && player){
      const duration = player.duration;
      if(this.isValidDuration(duration)){
        this.videoTimeout = duration * 1000 + 1000; // 1 second buffer
        this.videoTimer = setTimeout(this.onEnd, this.videoTimeout);
      }
    }

    // Reported last, and defensively, on purpose. This tells the caller the media arrived
    // and play() was called -- not first paint, the play event fires on the call -- which
    // is how a silent stall is told apart from a video nobody started. Useful, but it is
    // observability, and it must not be able to skip the safety net above: a throw here
    // would leave this video with no backup timer AND, for the Ready Module, a watchdog
    // its own handler has already cleared. Nothing below depends on it.
    // Once per URL: onPlay also fires on resume after buffering.
    if(this.props.onPlaybackStarted && player && this.reportedPlaybackStartFor !== this.props.videoURL){
      this.reportedPlaybackStartFor = this.props.videoURL;
      try {
        this.props.onPlaybackStarted({ duration: player.duration });
      } catch (error) {
        logSessionInfo("video_playback_report_error", {
          videoURL: this.props.videoURL,
          error: (error && error.message) || String(error),
        });
      }
    }
  }

  /**
   * Monitors video playback progress. This serves as a fallback mechanism to detect
   * video completion in cases where the onEnded event fails to fire (known issue on
   * Chrome OS / Chromebooks where the hardware decoder may fail to trigger the ended event).
   */
  onTimeUpdate(event){
    const player = this.htmlVideoPlayerRef.current;
    if(!player || this.hasCalledOnEnd) return;
    
    const currentTime = player.currentTime;
    const duration = player.duration;
    
    // Check if the video.ended property is true (can be set even if onEnded event doesn't fire)
    if(player.ended){
      this.onEnd();
      return;
    }
    
    // If the backup timer wasn't set in onPlay (because duration was invalid), try to set it now
    // This ensures we always have a safety net timeout
    // Use remaining time instead of full duration since we're already partway through
    if(!this.videoTimer && this.isValidDuration(duration)){
      const remainingTime = duration - currentTime;
      this.videoTimeout = remainingTime * 1000 + 1000; // remaining time + 1 second buffer
      this.videoTimer = setTimeout(this.onEnd, this.videoTimeout);
    }
    
    // Check if video has reached near the end (within 0.1 seconds of duration)
    if(this.isValidDuration(duration) && currentTime >= duration - 0.1){
      this.onEnd();
    }
  }

  simplePlayVideo(){
    // Reset flag in case this is a retry after previous play attempt
    this.hasCalledOnEnd = false;
    this.props.speech.abortSpeech();
    let player = this.htmlVideoPlayerRef.current;
    if (player) {
      player.play().catch((e)=>{
        const errorMessage = get(e, 'message')
        if(typeof errorMessage === 'string' && errorMessage.includes('The request is not allowed by the user agent')){
          console.log("handling play video error - will try to play with a play button", errorMessage)
          this.props.speech.speak({
            textToSpeak: COPY_STRINGS.play_video_with_button,
          })
          logSessionInfo("play_video_error", { videoURL: this.props.videoURL, error: errorMessage });
          this.setState({requirePlayButton: true, clickable: getUserData().sessionData.useIpadFallbacks}) // make the video clickable again so that the user can try to play the video
        }else{
          console.log("play video error", e)
        }
      })
    } else {
      logSessionInfo("play_video_error", { videoURL: this.props.videoURL, error: "Player not found in simplePlayVideo" });
      console.error("Play video error - Player not found in simplePlayVideo", this.props.videoURL);
      // log to sentry only if the prop is true to avoid quota limit errors
      if (this.props.logToSentry){
        Sentry.captureMessage("Play video error", { level: "warning"});
      }
    }
  }

  playVideo(){
    //don't allow the same video to replay
    if((this.props.videoURL === this.state.playedVideo) && !this.props.allowReplay){
      return;
    }
    
    // Reset flag for new play or replay - ensures onEnd can fire
    this.hasCalledOnEnd = false;

    prepareMicForPlayback();
    this.props.speech.cancelRecorderStreamIfNeeded();
    this.props.speech.abortSpeech();
    // Remove timeout logic from here - it will be handled in onPlay
    // This ensures timeout only starts when video actually begins playing

    let player = this.htmlVideoPlayerRef.current;
    if (player) {
      player.volume = 1.0;
    } else {
      logSessionInfo("play_video_error", { videoURL: this.props.videoURL, error: "Player not found" });
      console.error("Play video error - Player not found", this.props.videoURL);
      // log to sentry only if the prop is true to avoid quota limit errors
      if (this.props.logToSentry){
        Sentry.captureMessage("Play video error", { level: "warning"});
      }
      this.setState({requirePlayButton: true, clickable: getUserData().sessionData.useIpadFallbacks }); // make the video clickable again as per ipad fallbacks so that the user can try to play the video
      return;
    }
    this.setState({playedVideo: this.props.videoURL}, () => {
      player.play().catch((e)=>{
        prepareMicForRecord();
        this.props.speech.restartRecorderStreamIfNeeded();
        console.log("play video error", e)
        const errorMessage = get(e, 'message')
        if(typeof errorMessage === 'string' && errorMessage.includes('The request is not allowed by the user agent')){
          console.log("handling play video error - will try to play with a play button", errorMessage)
          this.props.speech.speak({
            textToSpeak: COPY_STRINGS.play_video_with_button,
          })
          logSessionInfo("play_video_error", { videoURL: this.props.videoURL, error: errorMessage });
          this.setState({requirePlayButton: true, clickable: getUserData().sessionData.useIpadFallbacks }); // make the video clickable again as per ipad fallbacks so that the user can try to play the video
        }
      })
    });
  }

  loadAndPlayVideo(){
    // Reset flag when starting a new video
    this.hasCalledOnEnd = false;
    
    let player = this.htmlVideoPlayerRef.current;
    //Set an event handler so we play the video once the data is loaded.
    this.htmlVideoPlayerRef.current.addEventListener('loadeddata', this.onLoadedData);
    player.load();
  }

  loadVideo(){
    // Reset flag when starting a new video
    this.hasCalledOnEnd = false;
    let player = this.htmlVideoPlayerRef.current;
    player.load();
  }

  cleanUp(){
    if(this.videoTimer){
      window.clearTimeout(this.videoTimer);
      this.videoTimer = null;
    }

    this.videoTimeout = 0;

    let player = this.htmlVideoPlayerRef.current;
    if (player) {
      player.removeEventListener('loadeddata', this.onLoadedData);
      player.removeEventListener('loadedmetadata', this.onLoadedMetadata);
      player.removeEventListener('play', this.onPlay);
    }
    if(this.props.unmountWhenDone){
      let player = this.htmlVideoPlayerRef.current;
      if (player) {
        player.pause();
        player.removeAttribute('src'); // empty source
        player.load();
      }
      //player.remove(); for now leave the element itself there so that the look of the UI remains more consistant
    }
  }

  /**
   * Handles video end event. This can be triggered by:
   * 1. The native onEnded event from the video element
   * 2. The timeupdate fallback when currentTime reaches duration
   * 3. The backup timeout timer
   * 
   * Uses hasCalledOnEnd flag to ensure this only executes once per video playback,
   * preventing duplicate callbacks from multiple trigger sources.
   */
  onEnd(){
    // Guard against multiple calls - can happen if both onEnded and timeupdate trigger,
    // or if the backup timer fires after the video has already ended
    if(this.hasCalledOnEnd) return;
    this.hasCalledOnEnd = true;
    
    prepareMicForRecord();
    this.props.speech.restartRecorderStreamIfNeeded();
    this.cleanUp();

    if(this.props.onDone){
      this.props.onDone();
    }
  }

  onError(){
    prepareMicForRecord();
    this.props.speech.restartRecorderStreamIfNeeded();
    if(this.props.onError){
      this.props.onError();
    }
  }

  preventEvent(e){
    e.preventDefault();
    e.stopPropagation();
    e.nativeEvent.stopImmediatePropagation();
  }

  //Keep the user from clicking on the video for play/pause/full screen
  handleClick(e){
    this.preventEvent(e);
    if(this.state.requirePlayButton){
      this.cleanUp();
      this.simplePlayVideo()
    } else if(this.state.clickable){
      this.playVideo();
    }
    this.setState({
      clickable: false,
      requirePlayButton: false
    });
  }

  componentWillUnmount(){
    this.cleanUp();
  }

  render() {
    let videoURL = this.props.videoURL;
    if((this.props.videoURL) && process.env.ASSET_ROOT){
      videoURL = this.props.videoURL.replace("s3.amazonaws.com/amira-assets",process.env.ASSET_ROOT);
    }

    return (
      <div className="studentVideoPlayer"
           {...doNotUseAccessibleStaticElementInteractions({
             onClick: this.handleClick,
             onDoubleClick:this.preventEvent,
             onMouseOver: this.state.clickable ? null : this.preventEvent
           })}>
        {(this.state.clickable || this.state.requirePlayButton) ?
          <div className="playButtonOverlay">
            <FaPlayCircle className="playButton" size="24"
              {...doNotUseAccessibleStaticElementInteractions({onClick: this.handleClick})}/>
          </div>
        : null}
        <video
          playsInline
          className="htmlVideoPlayer"
          onEnded={this.onEnd}
          onPlay={this.onPlay}
          onTimeUpdate={this.onTimeUpdate}
          //onPause={this.onEnd} //this is ok for now because we are not allowing users to pause the video
          onError={this.onError}
          key="theOnlyHtmlVideoPlayer"
          ref={this.htmlVideoPlayerRef} // Use the new ref created with React.createRef()
          volume={1}>
          <source src={videoURL} type="video/mp4"/>
        </video>
      </div>
    );
  }
}

StudentVideo.defaultProps = {
  autoPlay: true,
  // Every existing caller plays short reader clips, so the historical ceiling stays the
  // default. See isValidDuration for what a caller loses by exceeding it.
  maxDurationSeconds: DEFAULT_MAX_DURATION_SECONDS,
}

export default StudentVideo;